import { sanitizeMjcfName } from "./mjcfNames";

/** How the camera is created — drives the descriptive name stem before `_1`, `_2`, …. */
export type NextCameraNameContext =
  | { kind: "world" }
  | { kind: "mounted_body"; bodyName: string }
  | { kind: "mounted_site"; siteName: string; parentBodyName?: string };

function slug(label: string, fallback: string): string {
  const s = sanitizeMjcfName(label.trim()) || sanitizeMjcfName(fallback) || fallback;
  return String(s).toLowerCase();
}

/**
 * Stem before the numeric suffix, e.g. `world_camera`, `base_link_camera`,
 * `tool0_gripper_target_camera`.
 */
export function cameraNameStem(ctx: NextCameraNameContext): string {
  switch (ctx.kind) {
    case "world":
      return "world_camera";
    case "mounted_body":
      return `${slug(ctx.bodyName, "body")}_camera`;
    case "mounted_site": {
      const siteSlug = slug(ctx.siteName, "site");
      if (ctx.parentBodyName?.trim()) {
        return `${slug(ctx.parentBodyName, "body")}_${siteSlug}_camera`;
      }
      return `${siteSlug}_site_camera`;
    }
  }
}

type CameraRecordMap = Record<string, { name?: string } | null | undefined>;

/** Next `{stem}_{n}` not already used as a scene camera name. */
export function nextAvailableCameraName(
  camerasByUuid: CameraRecordMap | undefined,
  ctx: NextCameraNameContext
): string {
  const stem = cameraNameStem(ctx);
  const names = new Set(
    Object.values(camerasByUuid || {})
      .map((c) => String(c?.name || "").trim())
      .filter(Boolean)
  );
  let n = 1;
  let candidate = `${stem}_${n}`;
  while (names.has(candidate)) {
    n += 1;
    candidate = `${stem}_${n}`;
  }
  return candidate;
}
